import React, { useState } from 'react';
import { 
  Container, Row, Col, Card, CardBody, CardHeader, FormGroup, 
  Label, Input, Button, Alert, Table, Progress, Badge 
} from 'reactstrap';

export default function SkillSelectCalculator() {
  const [formData, setFormData] = useState({
    age: '', english: '', experience: '', overseasExperience: '',
    education: '', australianStudy: '', specialistEducation: false,
    credentialCommunity: false, regionalStudy: false, partnerSkills: '',
    pte: false, naati: false, professionalYear: false, sponsorship: ''
  });

  const [totalPoints, setTotalPoints] = useState(0);
  const [breakdown, setBreakdown] = useState([]);

  // LOGIC CONSTANTS
  const agePoints = { '18-24': 25, '25-32': 30, '33-39': 25, '40-44': 15, '45+': 0 };
  const englishPoints = { 'competent': 0, 'proficient': 10, 'superior': 20 };
  const experiencePoints = { 'none': 0, '1-2': 5, '3-4': 10, '5-7': 15, '8+': 20 };
  const overseasExpPoints = { 'none': 0, '3-4': 5, '5-7': 10, '8+': 15 };
  const educationPoints = { 'diploma': 10, 'bachelor': 15, 'phd': 20 };
  const australianStudyPoints = { 'no': 0, 'yes': 5 };
  const partnerPoints = { 'none': 0, 'skilled': 10, 'competent': 5, 'single': 10 };
  const sponsorshipPoints = { 'none': 0, 'state': 5, 'regional': 15 };

  const calculatePoints = () => {
    let points = 0;
    let details = [];
    if (formData.age) { points += agePoints[formData.age]; details.push({ category: 'Age', points: agePoints[formData.age] }); }
    if (formData.english) { points += englishPoints[formData.english]; details.push({ category: 'English Language', points: englishPoints[formData.english] }); }
    if (formData.experience) { points += experiencePoints[formData.experience]; details.push({ category: 'AU Work Experience', points: experiencePoints[formData.experience] }); }
    if (formData.overseasExperience) { points += overseasExpPoints[formData.overseasExperience]; details.push({ category: 'Overseas Experience', points: overseasExpPoints[formData.overseasExperience] }); }
    if (formData.education) { points += educationPoints[formData.education]; details.push({ category: 'Education', points: educationPoints[formData.education] }); }
    if (formData.australianStudy === 'yes') { points += 5; details.push({ category: 'Australian Study', points: 5 }); }
    if (formData.specialistEducation) { points += 10; details.push({ category: 'Specialist Education', points: 10 }); }
    if (formData.credentialCommunity) { points += 5; details.push({ category: 'Credential Community Language', points: 5 }); }
    if (formData.regionalStudy) { points += 5; details.push({ category: 'Regional Study', points: 5 }); }
    if (formData.partnerSkills) { points += partnerPoints[formData.partnerSkills]; if(partnerPoints[formData.partnerSkills] > 0) details.push({ category: 'Partner Skills', points: partnerPoints[formData.partnerSkills] }); }
    if (formData.professionalYear) { points += 5; details.push({ category: 'Professional Year', points: 5 }); }
    if (formData.sponsorship) { points += sponsorshipPoints[formData.sponsorship]; if(sponsorshipPoints[formData.sponsorship] > 0) details.push({ category: 'Sponsorship', points: sponsorshipPoints[formData.sponsorship] }); }

    setTotalPoints(points);
    setBreakdown(details);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleReset = () => {
    setFormData({
      age: '', english: '', experience: '', overseasExperience: '',
      education: '', australianStudy: '', specialistEducation: false,
      credentialCommunity: false, regionalStudy: false, partnerSkills: '',
      pte: false, naati: false, professionalYear: false, sponsorship: ''
    });
    setTotalPoints(0);
    setBreakdown([]);
  };


  const cardStyle = { borderRadius: '16px', border: '1px solid #eee', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' };
  const inputStyle = { borderRadius: '8px', border: '1px solid #ced4da', padding: '0.6rem', fontSize: '0.95rem' };

  return (
    <div style={{ backgroundColor: '#fcfaff', minHeight: '100vh', fontFamily: '"Poppins", sans-serif' }}>
      
      {/* Brand Header */}
      <div style={{ background: `linear-gradient(135deg, #7e1790, #5e116b 100%)`, padding: '60px 0', color: 'white', textAlign: 'center' }}>
        <Container>
          <h1 className="fw-bold mb-2" style={{ letterSpacing: '-1px' }}>Australian PR Points Calculator</h1>
          <p style={{ opacity: 0.8, maxWidth: '600px', margin: '0 auto' }}>Calculate your points for Skilled Independent (189), Skilled Nominated (190), and Skilled Regional (491) visas.</p>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="g-4">
          <Col lg={8}>
            <Card style={cardStyle} className="border-0">
              <CardHeader className="bg-white border-bottom-0 pt-4 px-4">
                <h5 className="fw-bold mb-0" style={{ color: "#7e1790"}}>Step 1: Your Profile Details</h5>
              </CardHeader>
              <CardBody className="px-4 pb-5">
                <Row className="mb-4">
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-bold text-muted">AGE GROUP</Label>
                      <Input type="select" name="age" value={formData.age} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Range</option>
                        <option value="18-24">18-24 years (25 pts)</option>
                        <option value="25-32">25-32 years (30 pts)</option>
                        <option value="33-39">33-39 years (25 pts)</option>
                        <option value="40-44">40-44 years (15 pts)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-bold text-muted">ENGLISH LEVEL</Label>
                      <Input type="select" name="english" value={formData.english} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Level</option>
                        <option value="competent">Competent (0 pts)</option>
                        <option value="proficient">Proficient (10 pts)</option>
                        <option value="superior">Superior (20 pts)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <h5 className="fw-bold mb-3" style={{ color:"#7e1790" }}>Step 2: Experience & Education</h5>
                <Row className="mb-4">
                  <Col md={6} className="mb-3">
                    <FormGroup>
                      <Label className="small fw-bold text-muted">AU WORK EXP</Label>
                      <Input type="select" name="experience" value={formData.experience} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Years</option>
                        <option value="none">None</option>
                        <option value="1-2">1-2 years (5 pts)</option>
                        <option value="3-4">3-4 years (10 pts)</option>
                        <option value="5-7">5-7 years (15 pts)</option>
                        <option value="8+">8+ years (20 pts)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6} className="mb-3">
                    <FormGroup>
                      <Label className="small fw-bold text-muted">OVERSEAS WORK EXP</Label>
                      <Input type="select" name="overseasExperience" value={formData.overseasExperience} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Years</option>
                        <option value="none">None</option>
                        <option value="3-4">3-4 years (5 pts)</option>
                        <option value="5-7">5-7 years (10 pts)</option>
                        <option value="8+">8+ years (15 pts)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={12}>
                    <FormGroup>
                      <Label className="small fw-bold text-muted">HIGHEST QUALIFICATION</Label>
                      <Input type="select" name="education" value={formData.education} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Qualification</option>
                        <option value="diploma">Diploma / Trade (10 pts)</option>
                        <option value="bachelor">Bachelor Degree (15 pts)</option>
                        <option value="phd">PhD / Masters Research (20 pts)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="bg-light p-4 rounded-3 mb-4 border-start" style={{ borderLeft: `4px solid #f7941d` }}>
                  <Label className="small fw-bold text-muted mb-3 d-block">BONUS POINTS CHECKLIST</Label>
                  <Row>
                    <Col md={6}>
                      <FormGroup check className="mb-2"><Label check className="small fw-semibold"><Input type="checkbox" name="australianStudy" checked={formData.australianStudy === 'yes'} onChange={(e)=>setFormData({...formData, australianStudy: e.target.checked ? 'yes':'no'})} /> AU Study (2+ Years)</Label></FormGroup>
                      <FormGroup check className="mb-2"><Label check className="small fw-semibold"><Input type="checkbox" name="specialistEducation" checked={formData.specialistEducation} onChange={handleChange} /> Specialist STEM Education</Label></FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup check className="mb-2"><Label check className="small fw-semibold"><Input type="checkbox" name="regionalStudy" checked={formData.regionalStudy} onChange={handleChange} /> Regional Study</Label></FormGroup>
                      <FormGroup check className="mb-2"><Label check className="small fw-semibold"><Input type="checkbox" name="credentialCommunity" checked={formData.credentialCommunity} onChange={handleChange} /> NAATI Accreditation</Label></FormGroup>
                      <FormGroup check className="mb-2"><Label check className="small fw-semibold"><Input type="checkbox" name="professionalYear" checked={formData.professionalYear} onChange={handleChange} /> Professional Year</Label></FormGroup>
                    </Col>
                  </Row>
                </div>

                <h5 className="fw-bold mb-3" style={{ color: "#7e1790" }}>Step 3: Partner & Sponsorship</h5>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-bold text-muted">PARTNER STATUS</Label>
                      <Input type="select" name="partnerSkills" value={formData.partnerSkills} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Status</option>
                        <option value="single">Single / Citizen Partner (10 pts)</option>
                        <option value="skilled">Skilled Partner (10 pts)</option>
                        <option value="competent">Partner (English Only) (5 pts)</option>
                        <option value="none">No/Unskilled Partner (0 pts)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-bold text-muted">VISA TYPE / SPONSORSHIP</Label>
                      <Input type="select" name="sponsorship" value={formData.sponsorship} onChange={handleChange} style={inputStyle}>
                        <option value="">Select Visa</option>
                        <option value="none">Skilled Independent 189 (0 pts)</option>
                        <option value="state">State Nominated 190 (5 pts)</option>
                        <option value="regional">Regional Sponsored 491 (15 pts)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex gap-3 mt-4">
                  <Button 
                    onClick={calculatePoints} 
                    className="flex-grow-1 border-0 py-3 fw-bold shadow-sm"
                    style={{ backgroundColor: '#7e1790', borderRadius: '10px' }}
                  >
                    CALCULATE POINTS 🇦🇺
                  </Button>
                  <Button outline onClick={handleReset} className="px-4" style={{ borderRadius: '10px', borderColor: '#ddd' }}>Reset</Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Sidebar Results */}
          <Col lg={4}>
            <div style={{ position: 'sticky', top: '100px' }}>
              <Card className="text-white border-0 shadow-lg text-center p-4 mb-4" style={{ borderRadius: '20px', backgroundColor: '#7e1790' }}>
                <p className="small text-uppercase mb-1 fw-bold" style={{ opacity: 0.7 }}>Potential Score</p>
                <div className="display-1 fw-bold mb-2">{totalPoints}</div>
                <div className="px-3 mb-4">
                  <Progress value={Math.min(100, (totalPoints/85)*100)} color="warning" style={{ height: '8px', borderRadius: '10px' }} />
                  <div className="d-flex justify-content-between small mt-2 fw-semibold">
                    <span>Target: 65</span>
                    <span>High Chance: 85+</span>
                  </div>
                </div>
                {totalPoints >= 65 ? (
                  <div className="bg-white text-dark rounded-3 p-3">
                    <h6 className="fw-bold mb-1" style={{ color: '#2ecc71' }}>✓ Eligible for EOI</h6>
                    <p className="small mb-0 text-muted">You meet the minimum threshold of 65 points.</p>
                  </div>
                ) : (
                  <div className="bg-white text-dark rounded-3 p-3" style={{ opacity: 0.9 }}>
                    <h6 className="fw-bold mb-1 text-danger">Points Required</h6>
                    <p className="small mb-0 text-muted">Submit an inquiry to learn how to boost your score.</p>
                  </div>
                )}
              </Card>

              {breakdown.length > 0 && (
                <Card style={cardStyle} className="border-0">
                  <CardBody>
                    <h6 className="fw-bold mb-3 border-bottom pb-2">Score Breakdown</h6>
                    <Table borderless size="sm">
                      <tbody>
                        {breakdown.map((item, i) => (
                          <tr key={i}>
                            <td className="small text-muted py-2">{item.category}</td>
                            <td className="text-end fw-bold py-2">+{item.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              )}

              <Alert style={{ backgroundColor: '#fff3e0', border: 'none', borderRadius: '12px' }} className="mt-4 d-flex">
                <span style={{ fontSize: '1.5rem', marginRight: '15px' }}>ℹ️</span>
                <span className="small text-muted">This calculator is an estimate. Government rules change; please consult with our authorized agents.</span>
              </Alert>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}